// DEPENDENCIES
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
// COMPONENTS
import Select from 'react-select'
import { NavLink, Link } from 'react-router-dom';
import MUIDataTable from 'mui-datatables';
import { Form } from 'react-bootstrap';
import { Spinner } from 'react-bootstrap';
import Chip from '@material-ui/core/Chip';
// CUSTOM COMPONENTS
import * as alert from '../../../redux/alertSnackBarRedux';
// SERVICES AND HELPERS
import * as credentialService from '../../../services/training/credentialService';
import * as trainingTypeService from '../../../services/companyData/trainingTypeService';
import * as trainingSubTypeService from '../../../services/companyData/trainingSubTypeService';
import * as userCredentialService from '../../../services/training/userCredentialService';
import { setToOptionModel, saveJSONToLocalStorage, getJSONFromLocalStorage } from '../../../helpers/helper';
import { dashIfNull } from '../../../helpers/helper';

const EmployeeSearchPage = (props) => {
    const { showAlert } = props;
    const [credentialTypeList, setCredentialTypeList] = useState([]);
    const [selectedCredentialType, setSelectedCredentialType] = useState(null);
    const [credentialSubTypeList, setCredentialSubTypeList] = useState([]);
    const [selectedCredentialSubType, setSelectedCredentialSubType] = useState(null);
    const [credentialList, setCredentialList] = useState([]);
    const [selectedCredential, setSelectedCredential] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [preLoading, setPreLoading] = useState(true);
    const [filters, setFilters] = useState([]);

    useEffect(() => {
        let searchModel = getJSONFromLocalStorage('employeeSearch');
        if (searchModel) {
            loadCachedSearchTerms(searchModel);
        } else {
            setPreLoading(false);
        }

        fetchTrainingType();
    }, []);

    const loadCachedSearchTerms = async (searchModel) => {
        setFilters(searchModel);
        searchUserByCredential(searchModel).then(res => {
            setPreLoading(false);
        }).catch(ex => {
            setPreLoading(false);
            setFilters([]);
            showAlert({ message: ex.message + 'Cached filters have been cleared.', variant: 'error' });
        });
    }

    const fetchTrainingType = () => {
        return trainingTypeService.getTrainingTypes(false).then(res => {
            let options = res.map(x => setToOptionModel(x.name, x.id));
            setCredentialTypeList(options);

            return options;
        }).catch(ex => showAlert({ message: ex.message, variant: 'error' }));
    }

    const fetchTrainingSubType = (credentialId) => {
        return trainingSubTypeService.getTrainingSubTypes(credentialId, false).then(res => {
            let options = res.map(x => setToOptionModel(x.name, x.id));
            options.push(setToOptionModel("No SubType", 0));
            setCredentialSubTypeList(options);

            return options;
        })
    }

    const fetchCredential = (typeId, subTypeId) => {
        return credentialService.getCredentialsByTypeAndSubType(typeId, subTypeId).then(res => {
            var options = res.map(x => setToOptionModel(x.name, x.id));
            setCredentialList(options);

            return options;
        })
    }

    const handleClear = () => {
        emptyDropDowns();
        setFilters([]);
        localStorage.removeItem('employeeSearch');
    }

    const emptyDropDowns = () => {
        setSelectedCredential(null);
        setSelectedCredentialType(null);
        setSelectedCredentialSubType(null);
        setCredentialSubTypeList([]);
        setCredentialList([]);
        setData([]);
    }

    const handleSearch = (filters) => {
        if (filters.length === 0) {
            setData([]);
            localStorage.removeItem('employeeSearch');
            return;
        }
        setLoading(true);
        setData([]);

        saveJSONToLocalStorage('employeeSearch', filters);
        searchUserByCredential(filters).catch(ex => {
            showAlert({ message: ex.message, variant: 'error' });
        }).finally(() => setLoading(false));
    }

    const searchUserByCredential = (credentialIds) => {
        return userCredentialService.searchEmployeeByCredential(credentialIds.map(x => x.id).join(",")).then(res => {
            setData(res);
        })
    }

    const handleCredentialTypeChange = (val) => {
        setSelectedCredentialSubType(null);
        setSelectedCredential(null);
        setSelectedCredentialType(val);
        fetchTrainingSubType(val.value);
    }

    const handleCredentialSubTypeChange = (val) => {
        setSelectedCredential(null);
        setSelectedCredentialSubType(val);
        fetchCredential(selectedCredentialType.value, val.value);
    }

    const addFilter = () => {
        let allFilters = [...filters];
        selectedCredential.forEach(credential => {
            if (!filters.map(x => x.id).includes(credential.value)) {
                allFilters.push({
                    id: credential.value,
                    name: selectedCredentialType.label + ' - ' + selectedCredentialSubType.label + ' - ' + credential.label
                });
            }
        })
        setFilters(allFilters);
        handleSearch(allFilters);
        emptyDropDowns();
    }

    const removeFilter = (id) => {
        let allFilters = [...filters].filter(x => x.id != id);

        setFilters(allFilters);
        handleSearch(allFilters);
    }

    const columns = [
        {
            name: "id",
            label: "Actions",
            options: {
                filter: false,
                sort: false,
                width: 1,
                print: false,
                download: false,
                customBodyRenderLite: (dataIndex) => {
                    return (
                        <NavLink
                            to={"/management/user-profile/" + data[dataIndex].id}
                            href="#"
                            className="btn btn-sm btn-info font-weight-bold py-2 px-3 px-xxl-5"
                        >
                            View
                        </NavLink>
                    );
                }
            },
        },
        {
            name: "fullName",
            label: "Name",
            options: {
                filterType: 'multiselect',
                sort: true,
                customBodyRenderLite: (dataIndex) => {
                    return (
                        <div className="tc-user-block">
                            <div className="user-info-block">
                                <Link to={"/management/user-profile/" + data[dataIndex].id} className="text-dark font-weight-bolder text-hover-primary mb-1 font-size-lg">
                                    {data[dataIndex].fullName}
                                </Link>
                            </div>
                        </div>
                    );
                }
            }
        },
        {
            name: "twicPin",
            label: "TWIC Pin#",
            options: {
                sort: true,
                customBodyRenderLite: (dataIndex) => dashIfNull(data[dataIndex].twicPin)
            }
        },
        {
            name: "safetyCouncilStudentId",
            label: "Safety Council Student ID",
            options: {
                sort: true,
                customBodyRenderLite: (dataIndex) => dashIfNull(data[dataIndex].safetyCouncilStudentId)
            }
        }, {
            name: "expiryDate",
            label: "Credential Expiry Date",
            options: {
                sort: true,
                customBodyRenderLite: (dataIndex) => {
                    return data[dataIndex].expiryDate.split(",").map(x => 
                        <p>{x}</p>
                    )
                }
            }
        }, {
            name: "disaId",
            label: "DISA ID #",
            options: {
                sort: true,
                customBodyRenderLite: (dataIndex) => dashIfNull(data[dataIndex].disaId)
            }
        }, {
            name: "aiId",
            label: "AI #",
            options: {
                sort: true,
                customBodyRenderLite: (dataIndex) => dashIfNull(data[dataIndex].aiId)
            }
        }
    ];

    return (
        <div className="row" >
            <div className="col-lg-12 col-xxl-12">
                <div className={`card card-custom card-stretch gutter-b}`}>
                    <div className="card-header border-0 py-5">
                        <h3 className="card-title align-items-start flex-column">
                            <span className="card-label font-weight-bolder text-dark">
                                Employee Search
                            </span>
                            <span className="text-muted mt-3 font-weight-bold fz-xs">
                                Search Employees by Credentials. <i><small>(Only credentials that are active and not in archived are searched)</small></i>
                            </span>
                        </h3>
                    </div>
                    {!preLoading ? <div className="container-fluid">
                        <div className="row d-flex col-lg-12 col-xl-12">
                            <div className="col-lg-4 col-xl-4">
                                <Form.Group>
                                    <Form.Label>Credential Type</Form.Label>
                                    <Select
                                        value={selectedCredentialType}
                                        options={credentialTypeList}
                                        onChange={handleCredentialTypeChange}
                                        name="trainingTypeId"
                                        placeholder="Select Credential Type"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-lg-4 col-xl-4">
                                <Form.Group>
                                    <Form.Label>Credential Sub Type</Form.Label>
                                    <Select
                                        isDisabled={credentialSubTypeList.length == 0}
                                        value={selectedCredentialSubType}
                                        options={credentialSubTypeList}
                                        onChange={handleCredentialSubTypeChange}
                                        name="trainingTypeId"
                                        placeholder="Select Credential Sub Type"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-lg-4 col-xl-4">
                                <Form.Group>
                                    <Form.Label>Credential</Form.Label>
                                    <Select
                                        isMulti
                                        closeMenuOnSelect={false}
                                        isDisabled={credentialList.length == 0}
                                        value={selectedCredential}
                                        options={credentialList}
                                        onChange={setSelectedCredential}
                                        name="trainingTypeId"
                                        placeholder="Select Credential"
                                    />
                                </Form.Group>
                            </div>
                        </div>
                        <div className="row col-lg-12 col-xl-12 search-clear-btn-group mt-lg-5 flex-row-reverse mb-lg-10">
                            <button
                                disabled={!(selectedCredentialType && selectedCredentialSubType && selectedCredential)}
                                className="btn btn-success font-weight-bolder font-size-sm mr-3"
                                onClick={addFilter}
                            >
                                Add
                            </button>{' '}
                            <button className="btn btn-secondary font-weight-bolder font-size-sm mr-3" onClick={handleClear}>
                                Clear Filters
                            </button>
                        </div>
                        <div className="container row">
                            {
                                filters.map(filter => <Chip
                                    key={filter.id}
                                    label={filter.name}
                                    style={{ margin: '5px' }}
                                    onDelete={() => removeFilter(filter.id)}
                                    color='secondary'
                                />)
                            }

                        </div>
                        <div className="col-lg-12 col-xxl-12" style={{ 'zIndex': 0 }}>
                            <MUIDataTable
                                data={data}
                                columns={columns}
                                options={{
                                    rowsPerPage: 25,
                                    rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                                    filter: false,
                                    selectableRows: 'none',
                                    downloadOptions: {
                                        filename: 'Employee_search.csv',
                                        filterOptions: {
                                            useDisplayedColumnsOnly: true,
                                            useDisplayedRowsOnly: true
                                        }
                                    },
                                    textLabels: {
                                        body: {
                                            noMatch: loading ?
                                                <Spinner animation="border" size="lg" variant="info" /> :
                                                'Sorry, no matching records found.',
                                        },
                                    }
                                }}
                            />
                        </div>
                    </div>
                        :
                        <div className="container d-flex justify-content-center align-items-center" style={{ height: 100 }}>
                            <Spinner animation="grow" size="lg" variant="info" />
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

export default connect(null, alert.actions)(EmployeeSearchPage);